import type { THookEventTypeLabelKey } from "./automation";
import type { IEntityOwner } from "./user";

// NOTE: NSAI-6936 — multi-step workflows as the internal automation endpoints
// (`/automation/workflows`, `/automation/workflow-runs`) return them. The enum
// values mirror the server's EWorkflowTriggerType / EWorkflowStepType /
// EWorkflowRunStatus / EWorkflowStepRunStatus in
// apps/nsai/server/src/automation/automation.types.ts.
export enum EWorkflowTriggerType {
  MANUAL = "manual",
  SCHEDULED = "scheduled",
  EVENT = "event",
}

export enum EWorkflowStepType {
  AGENT_TURN = "agent_turn",
  TOOL_CALL = "tool_call",
  APPROVAL_GATE = "approval_gate",
}

export enum EWorkflowRunStatus {
  RUNNING = "running",
  WAITING_APPROVAL = "waiting_approval",
  OK = "ok",
  ERROR = "error",
  CANCELLED = "cancelled",
  REJECTED = "rejected",
}

export enum EWorkflowStepRunStatus {
  RUNNING = "running",
  OK = "ok",
  ERROR = "error",
  SKIPPED = "skipped",
  WAITING_APPROVAL = "waiting_approval",
  APPROVED = "approved",
  REJECTED = "rejected",
}

export interface IWorkflowStep {
  uuid?: string;
  stepOrder: number;
  stepType: string;
  name: string;
  config: Record<string, unknown>;
  timeoutSeconds?: number | null;
}

export interface IWorkflow {
  uuid: string;
  userUuid: string;
  projectUuid: string;
  name: string;
  description: string | null;
  triggerType: string;
  triggerConfig: Record<string, unknown> | null;
  enabled: boolean;
  modelUuid: string;
  assistantUuid: string | null;
  timeoutSeconds: number;
  requestedSkillUuids: string[] | null;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastStatus: string | null;
  isRunning: boolean;
  createdAt: string;
  updatedAt: string;
  user?: IEntityOwner;
  // NOTE: Present on the single-workflow read (GET /automation/workflows/:uuid),
  // absent from the list, which stays one query per project.
  steps?: IWorkflowStep[];
}

export interface IWorkflowRun {
  uuid: string;
  workflowUuid: string;
  status: string;
  currentStepIndex: number;
  triggerType: string;
  error: string | null;
  output: string | null;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  approvedByUuid: string | null;
  approvedAt: string | null;
  createdAt: string;
  // NOTE: NSAI-5609 — set only when another agent's run started this one.
  // Optional because an older server omits the keys.
  parentRunUuid?: string | null;
  parentAutomationUuid?: string | null;
  // NOTE: NSAI-7883 — how many runs this run's steps started.
  childRunCount?: number;
}

export interface IWorkflowStepRun {
  uuid: string;
  workflowRunUuid: string;
  workflowStepUuid: string;
  stepOrder: number;
  stepType: string;
  status: string;
  input: string | null;
  output: string | null;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
}

export interface IWorkflowRunDetail {
  run: IWorkflowRun;
  stepRuns: IWorkflowStepRun[];
}

export interface IWorkflowStepPayload {
  stepOrder: number;
  stepType: EWorkflowStepType;
  name: string;
  config: Record<string, unknown>;
  timeoutSeconds?: number | undefined;
}

export interface ICreateWorkflowPayload {
  name: string;
  // NOTE: null clears the stored value on update — the server treats an
  // omitted field as "unchanged", so a cleared description or a trigger that
  // no longer needs a config has to be sent explicitly.
  description?: string | null | undefined;
  triggerType: EWorkflowTriggerType;
  triggerConfig?: Record<string, unknown> | null | undefined;
  modelUuid: string;
  timeoutSeconds?: number | undefined;
  steps: IWorkflowStepPayload[];
}

export type TUpdateWorkflowPayload = Partial<ICreateWorkflowPayload>;

export interface IUpdateWorkflowProps {
  uuid: string;
  data: TUpdateWorkflowPayload;
}

export interface IGetWorkflowRunsProps {
  uuid: string;
  limit?: number | undefined;
}

// NOTE: The editable shape of one step in the web and mobile builders. Every
// type's fields live side by side so switching a step's type keeps what was
// typed for the others; `buildWorkflowPayload` reads only the active type's.
export interface IWorkflowStepFormValues {
  name: string;
  stepType: EWorkflowStepType;
  prompt: string;
  toolName: string;
  parameters: string;
  message: string;
  // NOTE: Not edited in the builder, only carried through: the server replaces
  // every step on update and stores an omitted step timeout as null, so a
  // stored per-step timeout must be sent back or an unrelated edit wipes it.
  timeoutSeconds: number | null;
}

export interface IWorkflowFormValues {
  name: string;
  description: string;
  triggerType: EWorkflowTriggerType;
  cronExpression: string;
  timezone: string;
  eventType: string;
  modelUuid: string;
  timeoutSeconds: number;
  steps: IWorkflowStepFormValues[];
}

export interface IBuildWorkflowFormValuesProps {
  workflow?: IWorkflow | null | undefined;
  defaultTimezone: string;
}

export interface IWorkflowCronSchedule {
  cronExpression: string;
  timezone: string;
}

export interface IBuildWorkflowStepConfigProps {
  step: IWorkflowStepFormValues;
  parameters: Record<string, unknown>;
}

// NOTE: Either the payload or the index of the first tool_call step whose
// parameters are not a JSON object, so each form can pin the error on it.
export type TBuildWorkflowPayloadResult =
  | { isValid: true; payload: ICreateWorkflowPayload }
  | { isValid: false; invalidParametersIndex: number };

// NOTE: The translated messages the shared zod schema needs; each platform
// passes its own `t` output so the schema itself stays i18n-free.
export interface IWorkflowFormSchemaMessages {
  nameRequired: string;
  nameMaxLength: string;
  nameAlphanumeric: string;
  modelRequired: string;
  cronRequired: string;
  eventTypeRequired: string;
  stepNameRequired: string;
  promptRequired: string;
  toolNameRequired: string;
  stepsRequired: string;
  stepsMax: string;
  timeoutMin: string;
  timeoutMax: string;
}

export interface IWorkflowPermissionSubject {
  userUuid: string;
}

export interface IWorkflowPermissionFlags {
  currentUserUuid: string | undefined;
  hasAutomationWrite: boolean;
  hasAutomationDelete: boolean;
  hasAutomationManagerWrite: boolean;
  hasAutomationManagerDelete: boolean;
}

export interface IWorkflowItemPermissions {
  canRun: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canDecide: boolean;
}

export interface IResolveWorkflowItemPermissionsProps extends IWorkflowPermissionFlags {
  item: IWorkflowPermissionSubject;
}

export type TWorkflowStatusTone =
  | "success"
  | "danger"
  | "warning"
  | "info"
  | "neutral";

export type TWorkflowStatusLabelKey =
  | "workflows.runStatus.running"
  | "workflows.runStatus.waiting_approval"
  | "workflows.runStatus.ok"
  | "workflows.runStatus.error"
  | "workflows.runStatus.cancelled"
  | "workflows.runStatus.rejected"
  | "workflows.runStatus.skipped"
  | "workflows.runStatus.approved";

export type TWorkflowTriggerDescription =
  | { kind: "schedule"; text: string }
  | {
      kind: "event";
      eventType: string;
      labelKey: THookEventTypeLabelKey | null;
    }
  | { kind: "none" };

export interface IDescribeWorkflowTriggerProps {
  triggerType: string;
  triggerConfig: Record<string, unknown> | null | undefined;
  locale: string | undefined;
}
